import { useEffect, useRef, type RefObject } from 'react'
import {
  clampBarLevelForReactive,
  combineSpeechPeaks,
  computePeakLevel,
  computeSpeakingOpacity,
  computeTimeDomainPeak,
  FREQUENCY_PEAK_DIVISOR,
  REACTIVE_SILENT_BAR_LEVEL,
} from '@/pages/Meetings/utils/liveAudioLevels'
import { getInternalMeetingCaptureStream } from '@/pages/Meetings/utils/internalMeetingCaptureBridge'

const BAR_COUNT = 24
const BAR_TRACK_PX = 80
const BAR_MIN_PX = 6
const STREAM_WAIT_MS = 10_000

interface LiveAudioLevelBarsProps {
  streamRef?: RefObject<MediaStream | null>
  stream?: MediaStream | null
  active: boolean
  /** When true, fade and flatten bars until speech crosses the speaking threshold. */
  reactive?: boolean
  /** Opacity of the bar row while below {@link SPEAKING_LEVEL_THRESHOLD} in reactive mode. */
  silentOpacity?: number
  /** When false with reactive, only opacity reacts — bar heights follow the analyser. */
  reactiveFlatten?: boolean
  className?: string
}

function resolveStream(
  streamRef: RefObject<MediaStream | null> | undefined,
  stream: MediaStream | null | undefined
): MediaStream | null {
  return streamRef?.current ?? stream ?? getInternalMeetingCaptureStream()
}

/**
 * Symmetric level bars driven by an AnalyserNode on the live capture stream.
 * Bar heights update via DOM refs (not React state) so rAF stays smooth.
 */
export function LiveAudioLevelBars({
  streamRef,
  stream = null,
  active,
  reactive = false,
  silentOpacity = 0.35,
  reactiveFlatten = true,
  className,
}: LiveAudioLevelBarsProps) {
  const barRefs = useRef<(HTMLDivElement | null)[]>([])
  const containerRef = useRef<HTMLDivElement | null>(null)
  const waitRafRef = useRef<number | null>(null)
  const tickRafRef = useRef<number | null>(null)

  useEffect(() => {
    barRefs.current.forEach((el) => {
      if (el) {
        el.style.height = `${Math.max(BAR_MIN_PX, Math.round(REACTIVE_SILENT_BAR_LEVEL * BAR_TRACK_PX))}px`
      }
    })
    if (containerRef.current) {
      containerRef.current.style.opacity = reactive ? String(silentOpacity) : '1'
    }

    if (!active) {
      return undefined
    }

    let cancelled = false
    let audioCleanup: (() => void) | undefined
    const waitStarted = Date.now()

    const startAnalyser = (liveStream: MediaStream) => {
      const audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(liveStream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.65
      source.connect(analyser)
      const data = new Uint8Array(analyser.frequencyBinCount)
      const timeDomain = new Uint8Array(analyser.fftSize)

      const applyLevels = (levels: number[], opacity: number) => {
        if (containerRef.current) {
          containerRef.current.style.opacity = String(opacity)
        }
        for (let i = 0; i < BAR_COUNT; i += 1) {
          const el = barRefs.current[i]
          if (!el) continue
          const level = levels[i] ?? REACTIVE_SILENT_BAR_LEVEL
          el.style.height = `${Math.max(BAR_MIN_PX, Math.round(level * BAR_TRACK_PX))}px`
        }
      }

      const tick = () => {
        if (cancelled) return
        analyser.getByteFrequencyData(data)
        analyser.getByteTimeDomainData(timeDomain)
        const peak = combineSpeechPeaks(
          computePeakLevel(data, FREQUENCY_PEAK_DIVISOR),
          computeTimeDomainPeak(timeDomain)
        )
        const opacity = reactive ? computeSpeakingOpacity(peak, undefined, silentOpacity) : 1

        const half = Math.floor(BAR_COUNT / 2)
        const next: number[] = []
        for (let i = 0; i < half; i += 1) {
          const index = Math.floor((i / half) * data.length)
          const normalized = Math.min(1, data[index] / FREQUENCY_PEAK_DIVISOR)
          const level = 0.08 + normalized * 0.92
          const flatten = reactive && reactiveFlatten
          next.push(flatten ? clampBarLevelForReactive(level, peak) : level)
        }
        const mirrored = [...next.slice().reverse(), ...next]
        applyLevels(mirrored, opacity)
        tickRafRef.current = window.requestAnimationFrame(tick)
      }

      void (async () => {
        if (audioContext.state === 'suspended') {
          try {
            await audioContext.resume()
          } catch {
            // Analyser stays silent if resume fails.
          }
        }
        if (!cancelled) {
          tickRafRef.current = window.requestAnimationFrame(tick)
        }
      })()

      return () => {
        if (tickRafRef.current !== null) window.cancelAnimationFrame(tickRafRef.current)
        tickRafRef.current = null
        source.disconnect()
        void audioContext.close()
      }
    }

    const waitForStream = () => {
      if (cancelled) return
      const liveStream = resolveStream(streamRef, stream)
      if (liveStream) {
        audioCleanup = startAnalyser(liveStream)
        return
      }
      if (Date.now() - waitStarted > STREAM_WAIT_MS) return
      waitRafRef.current = window.requestAnimationFrame(waitForStream)
    }

    waitForStream()

    return () => {
      cancelled = true
      if (waitRafRef.current !== null) window.cancelAnimationFrame(waitRafRef.current)
      waitRafRef.current = null
      audioCleanup?.()
    }
  }, [streamRef, stream, active, reactive, silentOpacity, reactiveFlatten])

  return (
    <div
      ref={containerRef}
      className={[
        'flex h-20 w-full items-end justify-center gap-1',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ transition: reactive ? 'opacity 180ms ease-out' : undefined }}
      data-testid="live-audio-level-bars"
      aria-hidden
    >
      {Array.from({ length: BAR_COUNT }, (_, index) => (
        <div
          key={index}
          ref={(el) => {
            barRefs.current[index] = el
          }}
          className="w-1.5 shrink-0 rounded-full bg-indigo-600 dark:bg-indigo-400"
          style={{
            height: `${Math.max(BAR_MIN_PX, Math.round(REACTIVE_SILENT_BAR_LEVEL * BAR_TRACK_PX))}px`,
          }}
        />
      ))}
    </div>
  )
}
